// Parses AI text output into structured test cases.
// Extended to support Swagger/API Gherkin format, fence stripping, and parse validation.

export type TcFormat = 'normal' | 'gherkin' | 'tdd';

export interface ParsedNormalCase {
  id: string; title: string; type: string;
  preconditions: string; steps: string[];
  testData: string; expectedResult: string;
}

export interface ParsedGherkinCase {
  id: string; type: string; scenarioName: string;
  stepLines: string[];        // keyword-prefixed step strings for table display
  lines: string[];            // raw Gherkin lines (Scenario: header + steps) for converters
  featureName: string;
  backgroundLines?: string[]; // only on the first scenario when a Background: block exists
}

export interface ParsedTddCase {
  id: string; name: string; requirement: string; type: string;
  inputData: string; stepsSummary: string[];
  expectedOutput: string; passCriteria: string;
}

export interface ParseResult {
  format: TcFormat;
  normalCases?: ParsedNormalCase[];
  gherkinCases?: ParsedGherkinCase[];
  featureName?: string;
  tddCases?: ParsedTddCase[];
}

export interface ValidationResult {
  severity: 'ok' | 'warning' | 'error';
  title?: string;
  detail?: string;
  suggestion?: string;
}

// ─── Markdown fence stripping ─────────────────────────────────────────────────
// LLMs frequently wrap output in ```gherkin / ```feature / ``` fences.

export function stripMarkdownFences(text: string): string {
  if (!text) return text;
  const match = text.match(/```\w*\s*\n([\s\S]*?)```/);
  if (match) return match[1].trim();
  return text.replace(/^```\w*\s*\n/, '').replace(/\n```\s*$/, '').trim();
}

// ─── Parse quality validation ─────────────────────────────────────────────────

export function validateParseResult(
  rawText: string,
  parsed: ParseResult | null,
  types: string[]
): ValidationResult {
  if (!rawText?.trim()) {
    return {
      severity:   'error',
      title:      'Empty AI response',
      detail:     'The model returned an empty response.',
      suggestion: 'Try generating again. If this persists, check your API key or quota.',
    };
  }

  if (!parsed) {
    const hasFeature  = /^Feature:/im.test(rawText);
    const hasScenario = /^\s*Scenario:/im.test(rawText);
    if (hasFeature && !hasScenario) {
      return {
        severity:   'error',
        title:      'Feature found but no Scenarios parsed',
        detail:     'The output contains a Feature: block but no Scenario: definitions could be extracted.',
        suggestion: 'Try generating again. Add more context in Additional Info to help the model.',
      };
    }
    return {
      severity:   'error',
      title:      'Could not parse AI output',
      detail:     'The AI response did not match the expected format.',
      suggestion: 'Try again. If using Gherkin, add "Output ONLY valid Gherkin — no markdown code fences" to Additional Info.',
    };
  }

  const count =
    (parsed.gherkinCases?.length ?? 0) +
    (parsed.normalCases?.length  ?? 0) +
    (parsed.tddCases?.length     ?? 0);

  const minExpected = types.length * 3;
  if (types.length > 0 && count < minExpected) {
    return {
      severity:   'warning',
      title:      `Only ${count} test case${count !== 1 ? 's' : ''} found`,
      detail:     `Expected at least ${types.length * 5} (${types.join(' + ')} — 5 per type). The model generated fewer than requested.`,
      suggestion: 'Generate again for more, or export what was found.',
    };
  }

  return { severity: 'ok' };
}

// ─── Normal English parser ────────────────────────────────────────────────────
function parseNormal(text: string): ParsedNormalCase[] {
  text = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  // [#*\s]* around each label tolerates models that decorate labels with Markdown
  // (e.g. "**Title:**" or "## Test Case ID:") despite being told to use plain text.
  const rx = /[#*\s]*Test Case ID:[#*]*\s*(.+?)\n[#*\s]*Title:[#*]*\s*(.+?)\n(?:[#*\s]*Requirement:[#*]*[^\n]*\n)?[#*\s]*Test Type:[#*]*\s*(.+?)\n[#*\s]*Preconditions:[#*]*\s*([\s\S]+?)\n[#*\s]*Test Steps:[#*]*\s*([\s\S]+?)\n[#*\s]*Test Data:[#*]*\s*(.+?)\n[#*\s]*Expected Result:[#*]*\s*([^\n]+)/g;
  const cases: ParsedNormalCase[] = [];
  let m: RegExpExecArray | null;
  while ((m = rx.exec(text)) !== null) {
    const steps = m[5].replace(/\n\s*/g, '\n').trim()
      .split('\n')
      .map(l => l.replace(/^Step \d+\.\s*/, '').trim())
      .filter(Boolean);
    cases.push({
      id: m[1].trim(), title: m[2].trim(), type: m[3].trim(),
      preconditions: m[4].trim(), steps,
      testData: m[6].trim(), expectedResult: m[7].trim(),
    });
  }
  return cases;
}

// ─── Gherkin parser ───────────────────────────────────────────────────────────
// Handles TC_BDD_, TC_API_, TC_TDD_ ID formats.
// Handles @TC_API_NNN tags on the line before Scenario:.
// Captures raw `lines` for Playwright/Postman converters.
// Captures `backgroundLines` and attaches to the first scenario.

function parseGherkin(text: string): { cases: ParsedGherkinCase[]; featureName: string } {
  text = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  const rawLines  = text.split('\n');
  const cases: ParsedGherkinCase[] = [];
  let current: ParsedGherkinCase | null = null;
  let featureName   = '';
  let pendingTag:   string | null = null;
  const backgroundLines: string[] = [];
  let inBackground  = false;

  for (const line of rawLines) {
    const trimmed = line.trim();

    if (trimmed.startsWith('Feature:')) {
      featureName  = trimmed.slice('Feature:'.length).trim();
      inBackground = false;
      continue;
    }

    if (trimmed.startsWith('Background:')) {
      inBackground = true;
      backgroundLines.push(line);
      continue;
    }

    if (trimmed.startsWith('Scenario:') || trimmed.startsWith('Scenario Outline:')) {
      inBackground = false;
      if (current) cases.push(current);

      const keyword = trimmed.startsWith('Scenario Outline:') ? 'Scenario Outline' : 'Scenario';
      const header  = trimmed.slice(keyword.length + 1).trim();

      // ID: prefer @-tag seen on previous line, then inline bracket ID
      const idMatch = header.match(/\[?(TC_(?:BDD|API|TDD)_\d+)\]?/i);
      const id = pendingTag || (idMatch ? idMatch[1] : '');

      const typeMatch = header.match(/\[?(Positive|Negative|Edge)\]?/i);
      const type = typeMatch ? typeMatch[1] : '';

      const scenarioName = header
        .replace(/\[?TC_(?:BDD|API|TDD)_\d+\]?\s*/gi, '')
        .replace(/\[?(Positive|Negative|Edge)\]?\s*/gi, '')
        .replace(/^\s*[-–]\s*/, '').trim();

      current = {
        id:           id || `TC_${String(cases.length + 1).padStart(3, '0')}`,
        type:         type || '—',
        scenarioName,
        stepLines:    [],
        lines:        [line],   // first line is the Scenario: header
        featureName,
      };
      pendingTag = null;
      continue;
    }

    // Capture @TC_API_NNN / @TC_BDD_NNN / @TC_TDD_NNN tag
    const tagMatch = trimmed.match(/^@(TC_(?:API|BDD|TDD)_\d+)/i);
    if (tagMatch) {
      pendingTag = tagMatch[1];
      continue;
    }

    if (inBackground) {
      backgroundLines.push(line);
      continue;
    }

    if (current) {
      current.lines.push(line);
      const kw = line.match(/^\s+(Given|When|And|Then|But)\s+(.+)/i);
      if (kw) {
        const keyword = kw[1].charAt(0).toUpperCase() + kw[1].slice(1).toLowerCase();
        current.stepLines.push(`${keyword} ${kw[2].trim()}`);
      }
    }
  }

  if (current) cases.push(current);

  // Attach background lines to the first scenario so converters can reconstruct the full file
  if (cases.length > 0 && backgroundLines.length > 0) {
    cases[0].backgroundLines = backgroundLines;
  }

  return { cases, featureName };
}

// ─── TDD parser ───────────────────────────────────────────────────────────────
function parseTdd(text: string): ParsedTddCase[] {
  text = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  // Same Markdown-decoration tolerance as parseNormal's regex — see comment there.
  const rx = /[#*\s]*Test ID:[#*]*\s*(.+?)\n[#*\s]*Test Name:[#*]*\s*(.+?)\n[#*\s]*Requirement:[#*]*\s*(.+?)\n[#*\s]*Type:[#*]*\s*(.+?)\n[#*\s]*Input Data:[#*]*\s*([\s\S]+?)\n[#*\s]*Steps Summary:[#*]*\s*([\s\S]+?)\n[#*\s]*Expected Output:[#*]*\s*(.+?)\n[#*\s]*Pass Criteria:[#*]*\s*([^\n]+)/g;
  const cases: ParsedTddCase[] = [];
  let m: RegExpExecArray | null;
  while ((m = rx.exec(text)) !== null) {
    const stepsSummary = m[6].trim()
      .split(/(?<!\d)(?=\d+\.\s)/)
      .map(s => s.replace(/^\d+\.\s*/, '').trim())
      .filter(Boolean);
    cases.push({
      id: m[1].trim(), name: m[2].trim(), requirement: m[3].trim(), type: m[4].trim(),
      inputData: m[5].trim(), stepsSummary,
      expectedOutput: m[7].trim(), passCriteria: m[8].trim(),
    });
  }
  return cases;
}

// ─── Public entry point ───────────────────────────────────────────────────────
export function parseAiOutput(text: string, format: TcFormat): ParseResult | null {
  if (format === 'gherkin') {
    const { cases, featureName } = parseGherkin(text);
    if (!cases.length) return null;
    return { format, gherkinCases: cases, featureName };
  }
  if (format === 'tdd') {
    const cases = parseTdd(text);
    if (!cases.length) return null;
    return { format, tddCases: cases };
  }
  const cases = parseNormal(text);
  if (!cases.length) return null;
  return { format, normalCases: cases };
}
